import { ref } from "vue";
import type { StagedAttachment } from "@/components/MyStudents/Submissions/components/InternalComments/types";
import {
  getAttachmentName,
  getOversizeMessage,
  isOversize,
} from "@/components/MyStudents/Submissions/components/InternalComments/utils";
import { useUploadCommentFile } from "@/components/MyStudents/Submissions/composables/useUploadCommentFile";
import type { StoredFileUpload } from "@/components/MyStudents/Submissions/types";
import { fileName } from "@/util/s3";

const toStagedAttachment = (upload: StoredFileUpload | null | undefined): StagedAttachment | undefined =>
  upload
    ? {
        name: getAttachmentName(upload),
        size: upload.size,
        upload,
      }
    : undefined;

// Same upload flow as forms/SharedComponents/FileUpload.vue (s3Helper): normalize name → validate → upload.
export const useCommentAttachment = () => {
  const attachment = ref<StagedAttachment | undefined>();
  const error = ref<string | undefined>();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadCommentFile();

  const selectFiles = (files: File[]): void => {
    const oversize = files.filter(isOversize);
    const [validFile] = files.filter((file) => !isOversize(file));

    // One attachment per comment: a staged file is only replaced after it's removed.
    if (validFile && !attachment.value) {
      const normalized = new File([validFile], fileName(validFile.name), { type: validFile.type });
      attachment.value = { name: validFile.name, size: validFile.size, file: normalized };
    }
    error.value = getOversizeMessage(oversize.map(({ name, size }) => ({ name, size })));
  };

  const removeAttachment = (): void => {
    attachment.value = undefined;
    error.value = undefined;
  };

  const reset = (initial?: StoredFileUpload | null): void => {
    attachment.value = toStagedAttachment(initial);
    error.value = undefined;
  };

  // Uploads a newly picked file; an already stored attachment is re-sent as is.
  const resolveFileUpload = async (): Promise<StoredFileUpload | undefined> => {
    const staged = attachment.value;
    if (staged?.file) {
      return uploadFile({ file: staged.file, originalName: staged.name, size: staged.size });
    }
    return staged?.upload;
  };

  return {
    attachment,
    error,
    isUploading,
    selectFiles,
    removeAttachment,
    reset,
    resolveFileUpload,
  };
};
